import React, { useEffect, useRef, useState } from "react";
import { AddFlex, CustomText, Gap } from "../../reusableStyles";
import { Refresh, Search, SearchOutlined } from "@mui/icons-material";
import {
  inputHeight,
  primaryActionColor,
  secondaryActionColor,
} from "../../constants";
import { Status, isArrayNullOrUndefined, isNullOrUndefined } from "../../utils";
import useWindowSize from "../../Hooks/useWindowSize";
import Close from "@mui/icons-material/Close";
import { Checkbox, CircularProgress, Tooltip } from "@mui/material";
import { filter } from "lodash";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { IOSSwitch } from "../../CustomSwitch";
import { useDispatch } from "react-redux";
import { setPagesTabIndexAction } from "../../Store/appTempSlice";
import customClientAxios from "../../customClientAxios";
import { FetchOfferingsByStatus } from "../../Api/ApiEndPoints";
import { setUserFeatureDataAction } from "../../Store/UserFeaturesSlice";

const StyledSearchInput = styled.input`
  height: "100%";
  flex-grow: 1;
  border: none;
  background-color: transparent;
  border-radius: 4px 0 0 4px;
  padding: 0 15px;
  outline: none;
  &::placeholder {
    color: ${(props) => props.color};
  }
`;

function OfferingPagesTabHeader({
  featureKey,
  setUserFeaturesLocal,
  userFeaturesLocal,
  setLoading,
  setShowSalesDetails,
  loading,
  refreshingData,
  handleGetRevenueSaleData,
  handleGetTicketSaleData,
}) {
  const userFeatures = useSelector(
    (state) => state.userFeatures.userFeatures.features[featureKey.id]
  );
  const [tabs, setTabs] = useState([]);
  const { pagesTabSelectedIndex } = useSelector((state) => state.appTemp.data);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const dispatch = useDispatch();
  const { isDesktopView, isMobileView } = useWindowSize();
  const [showSeacrhbarInMobile, setShowSearchbarInMobile] = useState(false);
  const handleToggleSidebar = () => {
    setShowSearchbarInMobile((prev) => !prev);
  };

  const handlePublishUnpublishTabChange = async (index, tab) => {
    dispatch(setPagesTabIndexAction(index));

    let apiStatus;
    let status;
    switch (index) {
      case 0:
        apiStatus = Status.PUBLISHED;
        status = "publishedItems";
        break;
      case 1:
        apiStatus = Status.UNPUBLISHED;
        status = "unPublishedItems";
        break;
      case 2:
        apiStatus = Status.ARCHIVED;
        status = "archived";
        break;
      case 3:
        apiStatus = undefined;
        break;
      default:
        console.error("Invalid tab index for API call:", index);
        return;
    }
    if (!isArrayNullOrUndefined(userFeatures[status])) {
      return;
    }
    try {
      setLoading(true);
      const response = await customClientAxios().post(FetchOfferingsByStatus, {
        featureId: featureKey.id,
        status: apiStatus === "ALL" ? undefined : apiStatus,
      });

      if (response && response.data) {
        // Assuming setUserFeatureDataAction is an existing Redux action:
        // - It takes a payload like { featureId: string, data: object }
        // - The reducer for this action updates state.userFeatures.userFeatures.features[featureId] with the provided data.
        // - The response.data from FetchOfferingsByStatus is expected to be the complete object
        //   for features[featureKey.id], including all relevant item lists (publishedItems, unPublishedItems, etc.)
        //   correctly populated by the backend based on the 'status' parameter.
        dispatch(
          setUserFeatureDataAction({
            featureId: featureKey.id,
            status:
              apiStatus === Status.LIVE
                ? "publishedItems"
                : apiStatus === Status.UNPUBLISHED
                ? "unPublishedItems"
                : "archived",
            offerings: response.data.offerings,
          })
        );
      } else {
        // Handle cases where response or response.data might be missing or invalid.
        // This could also be an error notification to the user.
        console.error(
          "API response for FetchOfferingsByStatus is invalid or empty:",
          response
        );
        // Optionally, if enqueueSnackbar is available and appropriate here:
        // import { enqueueSnackbar } from "notistack";
        // enqueueSnackbar("Failed to fetch offerings: Invalid response from server.", { variant: "error" });
      }
    } catch (error) {
      console.error(`Error fetching offerings for status ${apiStatus}:`, error);
    }
  };

  const getPublishedUnpublishedTabs = () => {
    return ["Published", "Unpublished", "Archived", "All"];
  };
  const getAllUserFeatures = () => {
    if (isArrayNullOrUndefined(userFeatures?.items)) return [];
    return userFeatures.items.filter(
      (item) =>
        item.status !== Status.ARCHIVED && item.status !== Status.DELETED
    );
  };

  const getCurrentTabItems = () => {
    switch (pagesTabSelectedIndex) {
      case 0:
        return userFeatures?.publishedItems;
      case 1:
        return userFeatures?.unPublishedItems;
      case 2:
        return userFeatures?.archived;
      default:
        return [];
    }
  };

  const onSearchTextChanged = (searchText) => {
    if (searchText.length === 0) {
      setUserFeaturesLocal(getCurrentTabItems());
      return;
    }
    const filtered = getCurrentTabItems().filter((feature) => {
      return feature.name.toLowerCase().includes(searchText);
    });
    setUserFeaturesLocal(filtered);
  };

  const updateListBasedOnTab = () => {
    let items;
    switch (pagesTabSelectedIndex) {
      case 0:
        items = userFeatures?.publishedItems;
        setUserFeaturesLocal(items);
        return;

      case 1:
        items = userFeatures?.unPublishedItems;
        setUserFeaturesLocal(items);
        return;
      case 2:
        items = userFeatures?.archived;
        setUserFeaturesLocal(items);
        return;
      case 3:
        items = getAllUserFeatures();
        setUserFeaturesLocal(items);
        return;
      default:
        items = [];
    }
    setLoading(false);
  };

  const getUserFeatures = () => {
    return selectedTabIndex === 0
      ? userFeatures.publishedItems
      : userFeatures.unPublishedItems;
  };

  useEffect(() => {
    updateListBasedOnTab();
  }, [userFeatures, pagesTabSelectedIndex]);

  useEffect(() => {
    setTabs(
      ["Published", "Unpublished", "Archived"].map((elem, index) => {
        if (index === 0) {
          return `${elem} (${
            userFeatures.publishedItems ? userFeatures.publishedItems.length : 0
          })`;
        }
        if (index === 1) {
          return `${elem} (${
            userFeatures.unPublishedItems
              ? userFeatures.unPublishedItems.length
              : 0
          })`;
        }
        if (index === 2) {
          return `${elem} (${
            userFeatures.archived ? userFeatures.archived.length : 0
          })`;
        }
        return `All (${userFeatures.items.length})`;
      })
    );
  }, [userFeatures]);

  return (
    <>
      <AddFlex
        gap={isMobileView() ? "16px" : "12px"}
        wrap="wrap-reverse"
        alignItems="center"
        justify={!isMobileView() && "space-between"}
      >
        <BoxedTabs
          tabs={tabs}
          selectedTabIndex={pagesTabSelectedIndex}
          setSelectedTabIndex={handlePublishUnpublishTabChange}
        />
        {isDesktopView() && (
          <div style={{ width: "30%" }}>
            <Searchbar showSearchIcon={true} onChange={onSearchTextChanged} />
          </div>
        )}

        <AddFlex gap="16px" alignItems="center">
          <CustomText margin="0 0 0 5px" color="black">
            Show Sale Details
          </CustomText>
          <IOSSwitch
            sx={{ padding: "0px" }}
            onChange={async (e) => {
              if (e.target.checked) {
                await handleGetTicketSaleData();
              } else {
                setShowSalesDetails(e.target.checked);
              }
            }}
          />
          {loading && <CircularProgress size={14} />}
        </AddFlex>
        {isMobileView() && <Gap />}
        <Tooltip title="Refresh sales data">
          <AddFlex
            padding="4px"
            style={{ cursor: "pointer" }}
            onClick={() => handleGetRevenueSaleData(true)}
          >
            {refreshingData ? (
              <CircularProgress size={18} />
            ) : (
              <Refresh sx={{ fontSize: "18px" }} />
            )}
          </AddFlex>
        </Tooltip>
        {isMobileView() && (
          <>
            <AddFlex
              width="30px"
              justify="center"
              alignItems="center"
              height="30px"
              // backgroundColor={primaryActionColor}
              onClick={handleToggleSidebar}
              borderRadius="50%"
              border={"1px solid " + primaryActionColor}
            >
              <Search sx={{ color: primaryActionColor, fontSize: "16px" }} />
            </AddFlex>
          </>
        )}
      </AddFlex>

      {showSeacrhbarInMobile && (
        <AddFlex
          alignItems="center"
          margin="16px 0 0 0"
          style={{ width: "100%" }}
        >
          <Searchbar showSearchIcon={false} onChange={onSearchTextChanged} />
          <Close
            onClick={handleToggleSidebar}
            sx={{ color: "grey", margin: "0 10px" }}
          />
        </AddFlex>
      )}
    </>
  );
}

export default OfferingPagesTabHeader;

export const BoxedTabs = ({
  selectedTabIndex,
  margin,
  setSelectedTabIndex,
  tabs,
}) => {
  const { isMobileView } = useWindowSize();
  const getBorderRadius = (arrayLength, index) => {
    if (arrayLength === 1) {
      return "4px";
    }
    if (index === 0) {
      return "4px 0 0 4px";
    }
    if (index === arrayLength - 1) {
      return "0 4px 4px 0";
    }
    return "0px";
  };

  return (
    <AddFlex style={{ flexShrink: 0 }} wrap="wrap">
      {tabs.map((elem, index) => (
        <AddFlex
          padding={isMobileView() ? "9px 12px" : "9px 20px"}
          backgroundColor={selectedTabIndex === index && secondaryActionColor}
          borderRadius={getBorderRadius(tabs.length, index)}
          onClick={() => setSelectedTabIndex(index, elem)}
          style={{
            color: primaryActionColor,
            fontSize: "13px",
            cursor: "pointer",
            borderLeft: `1px solid ${"#BAD6FF"}`,
            borderTop: `1px solid ${"#BAD6FF"}`,
            borderBottom: `1px solid ${"#BAD6FF"}`,
            borderRight:
              index === tabs.length - 1 ? `1px solid ${"#BAD6FF"}` : "",
          }}
        >
          {elem}
        </AddFlex>
      ))}
    </AddFlex>
  );
};

export const Searchbar = ({
  color,
  isMobileView,
  onChange,
  placeholder,
  showSearchIcon,
  onWidgetClick,
  borderRadius,
  backgroundColor,
  borderColor,
  value,
}) => {
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter" || event.key === "Return") {
        buttonRef.current.click();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <AddFlex
      style={{ width: isMobileView && "100%", flexGrow: "1" }}
      height={inputHeight}
      borderRadius={borderRadius ? borderRadius : "4px"}
      backgroundColor={backgroundColor}
      border={`1px solid ${borderColor ? borderColor : "#dddddd"}`}
    >
      <StyledSearchInput
        color={color}
        value={value && value}
        placeholder={placeholder ? placeholder : "Search for offerings..|"}
        onChange={(e) => {
          if (isNullOrUndefined(onChange)) return;
          onChange(e.target.value);
        }}
      />
      {showSearchIcon && (
        <AddFlex
          alignItems="center"
          justify="center"
          borderRadius="0px 4px 4px 0px"
          style={{
            height: inputHeight,
            width: inputHeight,
            borderLeft: `1px solid ${borderColor ? borderColor : "#dddddd"}`,
          }}
          ref={buttonRef}
          onClick={() => {
            if (isNullOrUndefined(onWidgetClick)) return;
            onWidgetClick();
          }}
        >
          <SearchOutlined sx={{ color: color ? color : "grey" }} />
        </AddFlex>
      )}
    </AddFlex>
  );
};
